import type {ChatStatusItem, PageChangeNewPage, PageChangeOperation, PageChangeSetStatusItem} from '../chat-status';
import {isPageChangeSetStatusItem} from '../chat-status';
import type {ChangeConfirmations} from './change-confirmations';
import {renderCheckbox, setCheckboxDisabled} from './render-utils';

export type PageChangeSetLabels = Record<string, string>;

/** What a button on a card asks the server to do. Resolves to an error text to show, or null when it worked. */
export type PageChangeSetActions = {
    /** Sends the changes touching participant input the user ticked along. */
    apply: (set: PageChangeSetStatusItem, confirmed: string[]) => Promise<string | null>;
    skip: (set: PageChangeSetStatusItem, operationId: string) => Promise<string | null>;
    cancel: (set: PageChangeSetStatusItem) => Promise<string | null>;
    /** Builds a new page of the set again after its build failed. */
    retryPage: (set: PageChangeSetStatusItem, operationId: string) => Promise<string | null>;
    /** Lets participants open a new page of the set once it is built. */
    openPage: (set: PageChangeSetStatusItem, operationId: string) => Promise<string | null>;
    /** Stops a finished set's card from coming back when the chat is opened again. */
    dismiss: (set: PageChangeSetStatusItem) => Promise<void>;
    /** Reads the set again, which also lets the server notice an apply or a build whose job was lost. */
    refresh: (set: PageChangeSetStatusItem) => Promise<void>;
};

/** How often a set being applied, or building a new page, is read again, in case a push was missed or a job lost. */
const APPLYING_REFRESH_MS = 30_000;

type Options = {
    labels: PageChangeSetLabels;
    actions: PageChangeSetActions;
    currentPageId: () => string;
    isRunning: () => boolean;
    close: string;
    /** Scrolls to a change on the page. False when the page does not show it. */
    reveal: (set: PageChangeSetStatusItem, operation: PageChangeOperation) => boolean;
    /** Called when the buttons become available or unavailable, so the page's buttons can follow. */
    onBusyChange: () => void;
    confirmations: ChangeConfirmations;
    /** Moves focus to the message box, for when a closed card leaves no other card to land on. */
    focusComposer: () => void;
};

/**
 * The control strips of change sets in the chat: how many changes are staged on which page, one line per change
 * that jumps to it on the page, where its content is previewed, and the buttons that apply, cancel or skip them.
 * A finished set stays as a record of what was applied until it is dismissed, unless every change went in and it made
 * no new page, which leaves nothing on the card to read or press.
 *
 * Cards are drawn from the server's view of the set every time it changes. The buttons never change a card
 * themselves, the answer from the server does.
 */
export class ChangeSetCards {
    private signature = '';
    private readonly dismissed = new Set<string>();
    /**
     * Set id to the error the last button press on that card came back with, and the set version it was about.
     * It is announced once, when it first shows, not again each time progress redraws the card.
     */
    private readonly errors = new Map<string, {text: string; version: string; announced?: boolean}>();
    /** Set id to the version last announced, so only real changes are read out. */
    private readonly announced = new Map<string, string>();
    private readonly announcer: HTMLElement;
    private busy = false;
    private rendered = false;
    /** The control pressed last. Browsers drop focus from a button once it is disabled, so it is kept here. */
    private focusAfterAction: string | undefined;
    private statusItems: ChatStatusItem[] = [];
    private refreshTimer = 0;

    constructor(private readonly element: HTMLElement, private readonly options: Options) {
        this.element.className = 'howspace-ai-change-sets';
        this.element.hidden = true;
        // The cards are redrawn whole, so they are not a live region themselves. One sentence per change is.
        this.announcer = document.createElement('div');
        this.announcer.className = 'sr-only';
        this.announcer.setAttribute('aria-live', 'polite');
        this.element.after(this.announcer);
    }

    render(statusItems: ChatStatusItem[]): void {
        this.statusItems = statusItems;
        const pageId = this.options.currentPageId();
        // Changes waiting for approval are shown, and applied, only on the page they were staged for. Leaving the
        // page hides them, and coming back shows them again. An apply shows its progress on any page, and a finished
        // set stays as the record of what changed.
        const closed = statusItems
            .filter(isPageChangeSetStatusItem)
            .filter(set => !this.dismissed.has(set.id) && closesItself(set));
        closed.forEach(set => this.dismissed.add(set.id));
        const sets = statusItems
            .filter(isPageChangeSetStatusItem)
            .filter(set => !this.dismissed.has(set.id))
            .filter(set => set.status !== 'staged' || set.pageId === pageId);
        sets.forEach(set => {
            const error = this.errors.get(set.id);
            if (error && error.version !== versionOf(set)) {
                this.errors.delete(set.id);
            }
        });
        const signature = JSON.stringify([
            pageId,
            this.busy,
            [...this.errors],
            sets.map(set => [
                set.id,
                set.status,
                set.revision,
                set.pageTitle,
                set.operations,
                this.options.confirmations.confirmed(set),
            ]),
        ]);
        if (signature !== this.signature) {
            this.signature = signature;
            const focusKey = (document.activeElement as HTMLElement | null)?.dataset?.focusKey ?? this.focusAfterAction;
            this.element.replaceChildren(...sets.map(set => this.renderCard(set)));
            this.element.hidden = sets.length === 0;
            this.syncDisabled();
            this.restoreFocus(focusKey);
            this.announce([...sets, ...closed]);
            if (!this.busy) {
                this.focusAfterAction = undefined;
            }
        }
        this.syncDisabled();
        this.syncRefresh();
    }

    refresh(): void {
        this.render(this.statusItems);
    }

    /**
     * Whether a button press is waiting for the server.
     */
    isBusy(): boolean {
        return this.busy;
    }

    /**
     * Skip pressed on the page. It runs like the card's own Skip, so the card shows its error, but focus stays on
     * the page rather than jumping to the chat.
     */
    skipFromPage(set: PageChangeSetStatusItem, operationId: string): void {
        this.run(set, null, () => this.options.actions.skip(set, operationId));
    }

    /**
     * Whether one of the chat's sets is being applied. Its outcome reaches only this chat.
     */
    isApplying(): boolean {
        return this.applyingSets().length > 0;
    }

    /**
     * New chat waits while changes are applied, because their outcome reaches only this chat. The cards say so.
     */
    explainNewChatWaits(): void {
        const sets = this.applyingSets().filter(set => !this.dismissed.has(set.id));
        sets.forEach(set => this.errors.set(set.id, {text: this.label('newChatWaits'), version: versionOf(set)}));
        this.signature = '';
        this.render(this.statusItems);
    }

    /**
     * Applies and new page builds run in the background and report through pushes. While one runs, its set is also
     * read now and then, also when the user is on another page, so a missed push or a lost job does not leave it
     * running for good.
     */
    private syncRefresh(): void {
        if (this.runningSets().length === 0) {
            window.clearInterval(this.refreshTimer);
            this.refreshTimer = 0;
            return;
        }
        if (this.refreshTimer !== 0) {
            return;
        }
        this.refreshTimer = window.setInterval(() => {
            if (!this.element.isConnected) {
                window.clearInterval(this.refreshTimer);
                this.refreshTimer = 0;
                return;
            }
            this.runningSets().forEach(set => void this.options.actions.refresh(set));
        }, APPLYING_REFRESH_MS);
    }

    private runningSets(): PageChangeSetStatusItem[] {
        return this.statusItems.filter(isPageChangeSetStatusItem).filter(set => (
            set.status === 'applying'
            || set.operations.some(operation => (
                operation.newPage?.build === 'queued' || operation.newPage?.build === 'building'
            ))
        ));
    }

    private applyingSets(): PageChangeSetStatusItem[] {
        return this.statusItems.filter(isPageChangeSetStatusItem).filter(set => set.status === 'applying');
    }

    /**
     * Running replies come and go without the cards changing, so only the buttons follow them.
     */
    private syncDisabled(): void {
        const disabled = this.busy || this.options.isRunning();
        this.element.querySelectorAll<HTMLButtonElement>('button[data-action]').forEach(button => {
            button.disabled = disabled || button.dataset.empty === 'true';
        });
        this.element.querySelectorAll<HTMLInputElement>('input[data-confirm]').forEach(input => {
            setCheckboxDisabled(input, disabled);
        });
    }

    /**
     * A redraw replaces the focused button. Focus goes to the same control when it is still there, otherwise to
     * the first control of the same card, or its heading.
     */
    private restoreFocus(focusKey: string | undefined): void {
        if (!focusKey) {
            return;
        }
        const same = this.element.querySelector<HTMLElement>(`[data-focus-key="${CSS.escape(focusKey)}"]`);
        if (same) {
            same.focus();
            return;
        }
        const setId = focusKey.split(':')[1] || '';
        const card = this.element.querySelector<HTMLElement>(`[data-set-id="${CSS.escape(setId)}"]`);
        const fallback = card?.querySelector<HTMLElement>('button[data-action]:not(:disabled)')
            ?? card?.querySelector<HTMLElement>('.howspace-ai-change-set-heading');
        if (fallback) {
            fallback.focus();
        } else if (this.dismissed.has(setId)) {
            this.options.focusComposer();
        }
    }

    /**
     * Reads out what changed: the heading of a set that moved on, and how the build of each of its new pages goes,
     * which moves on without the set.
     */
    private announce(sets: PageChangeSetStatusItem[]): void {
        const changed = sets.filter(set => this.announced.get(set.id) !== announceKeyOf(set));
        sets.forEach(set => this.announced.set(set.id, announceKeyOf(set)));
        if (this.rendered && changed.length > 0) {
            this.announcer.textContent = changed.map(set => [
                this.headingText(set),
                ...set.operations
                    .filter(operation => operation.newPage !== null)
                    .map(operation => `${operation.title}: ${this.label(`build_${operation.newPage?.build}`)}`),
            ].join('. ')).join('. ');
        }
        this.rendered = true;
    }

    private renderCard(set: PageChangeSetStatusItem): HTMLElement {
        const card = document.createElement('section');
        card.className = `howspace-ai-change-set is-${set.status}`;
        card.dataset.setId = set.id;
        const heading = document.createElement('h3');
        heading.className = 'howspace-ai-change-set-heading';
        heading.id = `howspace-ai-change-set-${set.id}`;
        heading.tabIndex = -1;
        // Progress redraws the card while focus may rest here.
        heading.dataset.focusKey = `heading:${set.id}`;
        heading.textContent = this.headingText(set);
        card.setAttribute('aria-labelledby', heading.id);

        const headingRow = document.createElement('div');
        headingRow.className = 'howspace-ai-change-set-heading-row';
        headingRow.append(heading);
        if (set.status !== 'staged' && set.status !== 'applying') {
            headingRow.append(this.renderDismissButton(set));
        }
        card.append(headingRow);

        const list = document.createElement('ul');
        list.className = 'howspace-ai-change-set-list';
        set.operations
            .filter(() => set.status !== 'canceled')
            .filter(operation => set.status !== 'staged' || operation.status === 'pending')
            // A later edit of the same widget took its place and carries what it changed.
            .filter(operation => operation.reason !== 'replaced')
            .forEach(operation => list.append(this.renderOperation(set, operation)));
        if (list.childElementCount > 0) {
            card.append(list);
        }

        const error = this.errors.get(set.id);
        if (error) {
            const notice = document.createElement('p');
            notice.className = 'howspace-ai-change-set-error';
            if (!error.announced) {
                notice.setAttribute('role', 'alert');
                error.announced = true;
            }
            notice.textContent = error.text;
            card.append(notice);
        }

        if (set.status === 'staged') {
            card.append(this.renderActions(set));
        }

        return card;
    }

    private renderOperation(set: PageChangeSetStatusItem, operation: PageChangeOperation): HTMLElement {
        const item = document.createElement('li');
        item.className = `howspace-ai-change-set-item is-${operation.status}`;

        const icon = document.createElement('i');
        icon.className = `fa-light fa-${operation.icon || 'square'} howspace-ai-change-set-icon`;
        icon.setAttribute('aria-hidden', 'true');

        const body = document.createElement('div');
        body.className = 'howspace-ai-change-set-body';
        const title = operation.title || this.label('untitled');
        const pageChange = operation.op === 'update_item' && operation.widgetType === 'page';
        const lineText = pageChange
            ? this.pageChangeLines(operation)[0]
            : this.label(LINE_KEYS[operation.op] ?? 'lineAdd').replace('%title%', title);
        // A deleted widget is not on the page to follow once it is gone.
        const appliedOnPage = operation.status === 'applied'
            && operation.widgetId !== null
            && operation.op !== 'remove_item';
        // New pages and changes to the page itself are not on the page, so the card is where they are reviewed.
        const canReveal = operation.widgetType !== 'page'
            && set.pageId === this.options.currentPageId()
            && (set.status === 'staged' || appliedOnPage);
        if (canReveal) {
            const line = document.createElement('button');
            line.type = 'button';
            line.className = 'howspace-ai-change-set-line';
            line.textContent = lineText;
            line.setAttribute('aria-label', this.label('showOnPage').replace('%title%', lineText));
            line.dataset.focusKey = `line-${operation.id}:${set.id}`;
            line.addEventListener('click', () => {
                if (!this.options.reveal(set, operation)) {
                    this.errors.set(set.id, {text: this.label('notShown'), version: versionOf(set)});
                    this.render(this.statusItems);
                }
            });
            body.append(line);
        } else {
            const line = document.createElement('div');
            line.className = 'howspace-ai-change-set-title';
            line.textContent = lineText;
            body.append(line);
        }

        if (operation.op === 'add_page') {
            body.append(...this.renderNewPage(set, operation));
        }
        if (pageChange) {
            this.pageChangeLines(operation).slice(1).forEach(text => {
                const line = document.createElement('div');
                line.className = 'howspace-ai-change-set-title';
                line.textContent = text;
                body.append(line);
            });
        }

        if (set.status === 'staged' && operation.status === 'pending') {
            body.append(...this.renderReview(set, operation, title));
        }

        const outcome = this.outcomeText(operation);
        if (outcome) {
            const outcomeElement = document.createElement('p');
            outcomeElement.className = 'howspace-ai-change-set-outcome';
            outcomeElement.textContent = outcome;
            body.append(outcomeElement);
        }

        if (operation.newPage) {
            body.append(this.renderNewPageStatus(set, operation, operation.newPage));
        }

        item.append(icon, body);
        if (set.status === 'staged' && operation.status === 'pending') {
            const skip = this.renderButton(this.label('skip'), 'howspace-ai-change-set-skip', () => (
                this.options.actions.skip(set, operation.id)
            ), set, `skip-${operation.id}`);
            skip.setAttribute('aria-label', this.label('skipNamed').replace('%title%', title));
            item.append(skip);
        }

        return item;
    }

    /**
     * What stands in the way of a change waiting for approval: why it would not be applied as staged, or the tick it
     * needs because the widget has participant input. The same tick is on the page.
     */
    private renderReview(set: PageChangeSetStatusItem, operation: PageChangeOperation, title: string): HTMLElement[] {
        if (operation.blocked !== null) {
            const notice = document.createElement('p');
            notice.className = 'howspace-ai-change-set-blocked';
            notice.textContent = this.label(`blocked_${operation.blocked}`) || this.label(`reason_${operation.blocked}`);
            return [notice];
        }
        if (!operation.needsConfirmation) {
            return [];
        }

        const parts: HTMLElement[] = [];
        if (!operation.hadInput) {
            const notice = document.createElement('p');
            notice.className = 'howspace-ai-change-set-blocked';
            notice.textContent = this.label('inputSinceStaging');
            parts.push(notice);
        }
        const deletes = operation.op === 'remove_item';
        const {label, input} = renderCheckbox(this.label(deletes ? 'confirmDelete' : 'confirmEdit'));
        label.classList.add('howspace-ai-change-set-confirm');
        input.dataset.confirm = operation.id;
        input.dataset.focusKey = `confirm-${operation.id}:${set.id}`;
        input.checked = this.options.confirmations.isTicked(set, operation.id);
        setCheckboxDisabled(input, this.busy || this.options.isRunning());
        const namedKey = deletes ? 'confirmDeleteNamed' : 'confirmEditNamed';
        input.setAttribute('aria-label', this.label(namedKey).replace('%title%', () => title));
        input.addEventListener('change', () => this.options.confirmations.set(set, operation.id, input.checked));
        parts.push(label);

        return parts;
    }

    /**
     * What a change to the page itself does, one line per field: the new title, then who can open the page.
     */
    private pageChangeLines(operation: PageChangeOperation): string[] {
        const title = operation.title || this.label('untitled');
        const newTitle = operation.fields.title;
        const lines: string[] = [];
        if (newTitle) {
            lines.push(this.label('lineRenamePage').replace('%title%', title).replace('%new%', newTitle));
        }
        const access = operation.fields.access;
        if (access) {
            lines.push(this.label(access === 'participants' ? 'lineOpenPage' : 'lineLimitPage')
                .replace('%title%', newTitle || title));
        }

        return lines.length > 0 ? lines : [this.label('lineEdit').replace('%title%', title)];
    }

    /**
     * A new page waiting for approval shows its plan: what it is for, where it goes and its widgets. Pages are never
     * previewed in place, so the card is where they are reviewed.
     */
    private renderNewPage(set: PageChangeSetStatusItem, operation: PageChangeOperation): HTMLElement[] {
        if (set.status !== 'staged') {
            return [];
        }

        const parts: HTMLElement[] = [];
        if (operation.goal) {
            const goal = document.createElement('p');
            goal.className = 'howspace-ai-change-set-page-goal';
            goal.textContent = operation.goal;
            parts.push(goal);
        }

        const location = document.createElement('p');
        location.className = 'howspace-ai-change-set-page-meta';
        const kind = operation.location?.kind ?? 'next';
        const after = operation.location?.after ?? '';
        const locationKey = kind === 'newFolder' && after === '' ? 'location_newFolderNext' : `location_${kind}`;
        location.textContent = this.label(locationKey)
            .replace('%title%', operation.location?.title || this.label('untitled'))
            .replace('%after%', after);
        parts.push(location);

        if (operation.pageWidgets.length > 0) {
            const widgets = document.createElement('ul');
            widgets.className = 'howspace-ai-change-set-page-widgets';
            widgets.setAttribute('aria-label', this.label('pageWidgets'));
            operation.pageWidgets.forEach(widget => {
                const item = document.createElement('li');
                const icon = document.createElement('i');
                icon.className = `fa-light fa-${widget.icon || 'square'}`;
                icon.setAttribute('aria-hidden', 'true');
                item.append(icon, document.createTextNode(widget.title || this.label('untitled')));
                widgets.append(item);
            });
            parts.push(widgets);
        }

        const note = document.createElement('p');
        note.className = 'howspace-ai-change-set-page-meta';
        note.textContent = this.label(operation.pageWidgets.length > 0 ? 'newPageNote' : 'newEmptyPageNote');
        parts.push(note);

        return parts;
    }

    /**
     * A new page once it is made: how its build goes, a link to it, and the button that comes next, Try again after a
     * failed build or opening it to participants once it is built.
     */
    private renderNewPageStatus(
        set: PageChangeSetStatusItem,
        operation: PageChangeOperation,
        newPage: PageChangeNewPage,
    ): HTMLElement {
        const status = document.createElement('div');
        status.className = 'howspace-ai-change-set-page-status';

        const text = document.createElement('p');
        text.className = 'howspace-ai-change-set-outcome';
        text.textContent = [
            this.label(`build_${newPage.build}`),
            newPage.build === 'done' && newPage.open ? this.label('pageOpen') : '',
            newPage.build === 'done' && newPage.access === 'adminsOnly' ? this.label('pageAdminsOnly') : '',
        ].filter(Boolean).join('. ');
        status.append(text);

        const actions = document.createElement('div');
        actions.className = 'howspace-ai-change-set-actions';
        const link = document.createElement('a');
        link.className = 'howspace-ai-change-set-page-link';
        link.href = `/${encodeURIComponent(newPage.path)}`;
        link.textContent = this.label('openPage');
        const pageTitle = operation.title || this.label('untitled');
        link.setAttribute('aria-label', this.label('openPageNamed').replace('%title%', pageTitle));
        actions.append(link);
        if (newPage.build === 'failed') {
            actions.append(this.renderButton(this.label('retryPage'), 'howspace-ai-change-set-page-action', () => (
                this.options.actions.retryPage(set, operation.id)
            ), set, `retry-page-${operation.id}`));
        } else if (newPage.build === 'done' && newPage.access === 'adminsOnly') {
            const open = this.label('openToParticipants');
            actions.append(this.renderButton(open, 'howspace-ai-change-set-page-action', () => (
                this.options.actions.openPage(set, operation.id)
            ), set, `open-page-${operation.id}`));
        }
        status.append(actions);

        return status;
    }

    private renderActions(set: PageChangeSetStatusItem): HTMLElement {
        const actions = document.createElement('div');
        actions.className = 'howspace-ai-change-set-actions';
        // Only what will be applied counts: a change that no longer holds or waits for its tick is left out.
        const count = this.options.confirmations.applicableCount(set);
        const applyLabel = count === 0
            ? this.label('applyNone')
            : (count === 1 ? this.label('applyOne') : this.label('apply').replace('%count%', String(count)));
        const apply = this.renderButton(applyLabel, 'howspace-ai-change-set-apply', () => (
            this.applyAsReviewed(set)
        ), set, 'apply');
        apply.dataset.empty = String(count === 0);
        apply.disabled = apply.disabled || count === 0;
        actions.append(
            apply,
            this.renderButton(this.label('cancel'), 'howspace-ai-change-set-cancel', () => (
                this.options.actions.cancel(set)
            ), set, 'cancel'),
        );

        return actions;
    }

    /**
     * Reads the set again before applying it. A widget may have got participant input, or been saved, while the
     * card was open, and then the card is drawn again with the tick or reason that asks for instead. The approval
     * names the revision the card showed, so the server refuses changes the user has not seen.
     */
    private async applyAsReviewed(set: PageChangeSetStatusItem): Promise<string | null> {
        await this.options.actions.refresh(set).catch(() => undefined);
        const fresh = this.statusItems.filter(isPageChangeSetStatusItem).find(item => item.id === set.id) ?? set;
        if (fresh.revision !== set.revision || reviewOf(fresh) !== reviewOf(set)) {
            return this.label('changedSinceReview');
        }

        return this.options.actions.apply(set, this.options.confirmations.confirmed(set));
    }

    private renderButton(
        text: string,
        className: string,
        onClick: () => Promise<string | null>,
        set: PageChangeSetStatusItem,
        action: string,
    ): HTMLButtonElement {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = className;
        button.textContent = text;
        button.dataset.action = action;
        button.dataset.focusKey = `${action}:${set.id}`;
        // Every button asks the server to act on the set the card shows. While the agent is answering it may
        // still be adding to that set, so nothing is offered until it is done.
        button.disabled = this.busy || this.options.isRunning();
        button.addEventListener('click', () => this.run(set, action, onClick));

        return button;
    }

    /**
     * Runs a button's request. Every button waits for it, and the card is drawn again from the server's answer.
     */
    private run(set: PageChangeSetStatusItem, action: string | null, onClick: () => Promise<string | null>): void {
        if (this.busy || this.options.isRunning()) {
            return;
        }
        this.busy = true;
        this.focusAfterAction = action === null ? undefined : `${action}:${set.id}`;
        this.errors.delete(set.id);
        this.signature = '';
        this.element.querySelectorAll<HTMLButtonElement | HTMLInputElement>('button, input').forEach(element => {
            element.disabled = true;
        });
        this.options.onBusyChange();
        const version = versionOf(set);
        void onClick().then(error => {
            this.busy = false;
            if (error) {
                this.errors.set(set.id, {text: error, version: this.currentVersion(set.id) ?? version});
            }
            this.signature = '';
            this.render(this.statusItems);
            this.options.onBusyChange();
        });
    }

    private renderDismissButton(set: PageChangeSetStatusItem): HTMLButtonElement {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'howspace-ai-change-set-dismiss';
        button.dataset.focusKey = `dismiss:${set.id}`;
        button.setAttribute('aria-label', this.options.close || 'Close');
        button.innerHTML = '<i class="fa-light fa-xmark" aria-hidden="true"></i>';
        button.addEventListener('click', () => {
            this.dismissed.add(set.id);
            void this.options.actions.dismiss(set);
            this.errors.delete(set.id);
            const card = button.closest('.howspace-ai-change-set');
            // Focus lands on the card that takes its place, or the one above, before this one goes.
            const neighbour = card?.nextElementSibling ?? card?.previousElementSibling;
            const heading = neighbour?.querySelector<HTMLElement>('.howspace-ai-change-set-heading');
            if (heading) {
                heading.focus();
            } else {
                this.options.focusComposer();
            }
            card?.remove();
            this.element.hidden = this.element.childElementCount === 0;
        });

        return button;
    }

    private headingText(set: PageChangeSetStatusItem): string {
        const page = set.pageTitle || this.label('deletedPage');
        const count = pendingCount(set);
        // A staged card is shown only on its own page, so it does not name the page. A set that only makes new pages
        // changed no page it could name.
        const pagesOnly = set.operations.length > 0 && set.operations.every(operation => operation.op === 'add_page');
        const suffix = pagesOnly ? 'Pages' : '';
        const key = {
            staged: count === 1 ? 'headingOne' : 'heading',
            applying: `applying${suffix}`,
            completed: `completed${suffix}`,
            partly_applied: `partlyApplied${suffix}`,
            failed: `failed${suffix}`,
            canceled: 'canceled',
        }[set.status];

        return this.label(key)
            .replace('%count%', String(count))
            .replace('%done%', String(set.progress?.done ?? 0))
            .replace('%total%', String(set.progress?.total ?? 0))
            .replace('%page%', page);
    }

    private outcomeText(operation: PageChangeOperation): string {
        // A new page reports how it goes once it is made.
        if (operation.newPage) {
            return '';
        }
        const [done, notDone] = {
            update_item: ['statusChanged', 'statusNotChanged'],
            remove_item: ['statusDeleted', 'statusNotDeleted'],
        }[operation.op] ?? ['statusApplied', 'statusNotApplied'];
        const status = {
            applied: this.label(done),
            skipped: this.label('statusSkipped'),
            failed: this.label(notDone),
            blocked: this.label(notDone),
        }[operation.status] || '';
        // A change to a page has its own wording where the shared one speaks of a widget.
        const pageReason = operation.reason && operation.widgetType === 'page'
            ? this.label(`reason_${operation.reason}_page`)
            : '';
        const reason = pageReason || (operation.reason ? this.label(`reason_${operation.reason}`) : '');

        return [status, reason].filter(Boolean).join('. ');
    }

    private currentVersion(setId: string): string | null {
        const set = this.statusItems.filter(isPageChangeSetStatusItem).find(item => item.id === setId);
        return set ? versionOf(set) : null;
    }

    private label(key: string): string {
        return this.options.labels[key] || '';
    }
}

const LINE_KEYS: Record<string, string> = {
    update_item: 'lineEdit',
    remove_item: 'lineRemove',
    add_page: 'lineAddPage',
};

const versionOf = (set: PageChangeSetStatusItem): string => `${set.status}:${set.revision}`;

const announceKeyOf = (set: PageChangeSetStatusItem): string => versionOf(set) + set.operations
    .map(operation => (operation.newPage ? `|${operation.newPage.build}:${operation.newPage.access}` : ''))
    .join('');

const closesItself = (set: PageChangeSetStatusItem): boolean => (
    set.status === 'completed' && set.operations.every(operation => operation.newPage === null)
);

/** What the card asks of each waiting change: whether it needs a tick, and why it would not apply. */
const reviewOf = (set: PageChangeSetStatusItem): string => JSON.stringify(set.operations
    .filter(operation => operation.status === 'pending')
    .map(operation => [operation.id, operation.needsConfirmation, operation.blocked]));

const pendingCount = (set: PageChangeSetStatusItem): number => (
    set.operations.filter(operation => operation.status === 'pending').length
);
